import { useEffect, useMemo, useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";

import grapes_img from "../../assets/icons/grapes.png";

import "./styles/index.scss";

import {
  flexRender,
  getCoreRowModel,
  SortingState,
  PaginationState,
  ColumnFiltersState,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getExpandedRowModel,
  ColumnDef,
  OnChangeFn,
  FilterFn,
  FilterFns,
  SortingFn,
  sortingFns,
} from "@tanstack/react-table";

import FooterCell from "./FooterCell";
import useLabels from "../../hooks/useLabels";
import DebouncedInput from "./DebouncedInput";

import {
  RankingInfo,
  rankItem,
  compareItems,
} from "@tanstack/match-sorter-utils";
import { CTA, Tooltip } from "../Generic";
import ElementCard from "./ElementCard";
import CardFilters from "../CardFilters";
import { getItem } from "../../services/LocalStorage";
import { Icon } from "../../components/Icon";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  // store the itemRank info
  addMeta({
    itemRank,
  });
  // console.log("fuzzy", row, columnId, value);

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

const MultipleRowsFilterFn = (row, columnId, value) => {
  const array = [];
  const val = value
    ?.trim()
    ?.toLowerCase?.()
    ?.replace(/[^ 0-9A-Z]+/gi, ""); // clear non-alphanumeric characters

  const col = row.getAllCells()?.find((c) => c?.id.includes(columnId)) || null;
  const meta = col.column.columnDef?.meta;

  if (meta?.selector === "aggregate") {
    const len = row.original?.origins?.length || 0;
    for (let i = 0; i < len; i++)
      array.push(
        `${row.original?.origins?.[i]} ${row.original?.years?.[i]} ${row.original?.varieties?.[i]}`
      );
  }

  switch (columnId) {
    case "cellarman":
      const cellarmansColumn =
        row.getAllCells()?.find((c) => c?.id.includes("cellarman")) || null;
      if (cellarmansColumn !== null) {
        array.push(
          ...cellarmansColumn.column.columnDef?.meta?.array?.map(
            (c) => c?.username
          )
        );
      }
      break;
    case "lotLabel":
      if (
        meta?.array == "label" &&
        (row.original?.label || [])?.some((l) => l === value)
      )
        array.push(...row.original?.label);
      else if (Array.isArray(meta?.array)) {
        // trick because in tanks there is no label array, so meta?.array = lots
        const lot =
          meta?.array?.find((l) => l?.id === row.original?.batch_id) || null;
        if (lot && lot?.label && lot?.label?.some((l) => l === value))
          array.push(...lot?.label);
      }
      break;
    case "lotTanks":
      array.push(...(row.original?.tanks?.map((t) => t?.tank_name) || []));
      break;
    case "operationSrcTank":
      array.push(
        ...(row.original?.src_tanks?.map((t) => t?.name) ||
          [])
      );
      break;
    case "substance_name":
      const substanceColumn =
        row.getAllCells()?.find((c) => c?.id.includes("substance_name")) ||
        null;
      const substanceColumnType =
        substanceColumn === null
          ? null
          : substanceColumn.column.columnDef?.meta?.array || null;

      if (substanceColumn !== null && substanceColumnType !== null) {
        let filteredSubstances = [];
        if (substanceColumnType === "analysis") {
          const substanceColumnArray =
            substanceColumn.column.columnDef?.meta?.protocols?.map((p) => {
              return {
                substance_name: p?.name,
                translated_name: p?.translated_name,
              };
            });
          const currentSubstanceColumnArray = row.original?.[
            substanceColumnType
          ]?.map((a) => a?.substance_name);
          filteredSubstances.push(
            ...substanceColumnArray
              ?.filter((s) =>
                currentSubstanceColumnArray?.includes(s?.substance_name)
              )
              ?.map((a) => a?.translated_name)
          );
        } else {
          filteredSubstances.push(
            ...row.original?.[substanceColumnType]?.map(
              (s) => s?.substance_name
            )
          );
        }
        array.push(...filteredSubstances);
      }
      break;
    case "operationDestTank":
      array.push(
        ...(row.original?.dest_tanks?.map((t) => t?.name) ||
          [])
      );
      break;
    // case "substance_name":
    //   const protocolType =  row.original?.type;
    //   const array = row?.original?.[protocolType?.toLowerCase()]?.map((s) => s?.substance_name) || [];
    //   const lenS = row.original?.origins?.length || 0;
    //   console.log('sssff', protocolType, row?.original, '-->', array)
    //   for (let i = 0; i < lenS; i++)
    //     array.push(
    //       `${row.original?.origins?.[i]} `
    //     );
    //   break;
  }
  if (array?.length > 0 && val)
    return !!array?.find((item) => item?.toLowerCase?.().includes(val));

  return false;
};

const toDateInputValue = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const DateInRange = (row, columnId, value) => {
  // The accessor value may be a localized/formatted string (or a date range) that
  // `new Date()` can't parse, so allow reading the raw date from row.original via
  // meta.dateField; otherwise fall back to the column value.
  const col = row.getAllCells()?.find((c) => c?.id.includes(columnId)) || null;
  const dateField = col?.column?.columnDef?.meta?.dateField;
  const raw = dateField ? row.original?.[dateField] : row.getValue(columnId);
  const date = new Date(raw);

  const [startDate, endDate] = value; // value => two date input values
  const start = startDate ? new Date(startDate) : null;
  // make the end of the range inclusive to the whole day (e.g. [X, X] covers all of day X)
  const end = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : null;

  // if one filter defined and date is null filter it
  if ((start || end) && !date) return false;
  if (start && !end) {
    return date.getTime() >= start.getTime();
  } else if (!start && end) {
    return date.getTime() <= end.getTime();
  } else if (start && end) {
    return date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
  } else return true;
};

const QuantityInRange = (row, columnId, value) => {
  const quantity = parseInt(row.original?.actual_quantity) || null;
  if (quantity !== null && quantity >= 0) {
    return quantity >= value?.[0] && quantity <= value?.[1];
  }

  return false;
};

const fuzzySort = (rowA, rowB, columnId) => {
  let dir = 0;
  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank || "",
      rowB.columnFiltersMeta[columnId]?.itemRank || ""
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const PrioritySortingFn = (rowA, rowB, columnId) => {
  return rowA.original?.priority < rowB.original?.priority ? -1 : 1;
};

const TableEdit = ({
  defaultData = [],
  additionalData = [],
  columns,
  newRow,
  isPagination = false,
  isSearchBar = true,
  isEditable = false,
  isSorting = true,
  groupHandler = null,
  errors,
  control,
  setValue,
  getValues,
  rowClickHandler,
  analysisState,
  minEditableIndex,
  maxEditableIndex,
  type,
  modalHandler,
  setModifyMode,
  setRows,
  reset,
  setIsReset,
  filterType = null,
  isLegend = true,
  isMobile = false,
  removeRowHandler = null,
  totalVolume = 0,
  axiosPrivate,
  isWineMakingOperations = false,
  shouldSyncData = false,
  serverPagination = null,
  defaultClientPage = null,
}) => {
  const [getLabel] = useLabels();
  const [data, setData] = useState(() => [...defaultData]);
  const [originalData, setOriginalData] = useState(() => [...defaultData]);
  const [editedRows, setEditedRows] = useState({});
  const [sorting, setSorting] = useState([]);
  const [headerOver, setHeaderOver] = useState(-1);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: defaultClientPage !== null ? defaultClientPage : (Number(getItem(`${type}_tablePageIndex`)) || 0),
    pageSize: Number(getItem(`${type}_tablePageSize`)) || 10,
  });

  useEffect(() => {
    if (
      shouldSyncData ||
      !defaultData ||
      data?.length === 0 ||
      [
        "stock",
        "treatments",
        "caps",
        "bottles",
        "machineries",
        "filters",
        "winemaking",
        "PRE",
        "POST",
        "CIP",
        "SPARKLING",
        "PHYSICAL",
        "treatment",
        "analysis",
        "analysisForm",
        "analysisDetail",
        "operation",
        "treatmentApplication",
        "treatmentApplicationUser",
        "newTreatmentProtocol",
        "lot",
        "tanks",
        "permissions",
        "application",
        "templates",
      ].includes(type)
    ) {
      setData([...defaultData]);
      setOriginalData([...defaultData]);
    }
  }, [defaultData]);

  // useEffect(() => {
  //   // if (!data || data?.length === 0) {
  //     setData([...defaultData]);
  //     setOriginalData([...defaultData]);
  //   // }
  //   console.log('quiiii', data, originalData)
  // }, []);

  useEffect(() => {
    if (reset === true) {
      setData([]);
      setOriginalData([]);
      setIsReset(false);
    }
  }, [reset]);

  const table = useReactTable({
    data,
    columns,
    enableColumnFilters: true,
    enableFilters: true,
    enableColumnFilter: true,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      localStorage.setItem(`${type}_tablePageIndex`, next.pageIndex);
      localStorage.setItem(`${type}_tablePageSize`, next.pageSize);
      setPagination(next);
    },
    filterFns: {
      fuzzyFilter: fuzzyFilter,
      MultipleRowsFilterFn: MultipleRowsFilterFn,
      InNumberRange2: QuantityInRange,
      DateInRange: DateInRange,
    },
    sortingFns: {
      PrioritySortingFn: PrioritySortingFn,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    globalSortingFn: fuzzySort,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), // client-side faceting
    getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
    getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
    getExpandedRowModel: getExpandedRowModel(),
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: 2,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: isPagination
      ? getPaginationRowModel()
      : () => {
          return null;
        },
    enableRowSelection: true,
    meta: {
      minEditableIndex,
      maxEditableIndex,
      analysisState,
      editedRows,
      type,
      isWineMakingOperations,
      modalHandler,
      setModifyMode,
      setValue,
      setEditedRows,
      getTotalVolume: () => totalVolume,
      updatePermissionCell: (rowIndex, columnId, value) => {
        setData(old =>
          old.map((row, i) =>
            i !== rowIndex
              ? row
              : {
                  ...row,
                  [columnId]: {
                    ...row[columnId],
                    allowed: value,
                  },
                }
          )
        )
      },
      revertData: (rowIndex, revert, substanceId) => {
        // substanceId is the key in column map, rowIndex is the row position in the table
        const columnKey = substanceId || rowIndex;
        const columnData = getValues(`column.${columnKey}`);
        const oldValue = columnData?.old;

        if (revert) {
          // Cancel: restore old value
          setValue(`column.${columnKey}.value`, oldValue);
          setValue(`column.${columnKey}.new`, oldValue);

          // Restore the original row data
          setData((old) =>
            old.map((row, index) =>
              index === rowIndex ? originalData[rowIndex] : row
            )
          );
        } else {
          // Done: save current data as new original
          setOriginalData((old) =>
            old.map((row, index) => (index === rowIndex ? data[rowIndex] : row))
          );
        }
      },
      updateData: (rowIndex, columnId, value) => {
        setData((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
            return row;
          })
        );
      },
      getControl: () => control,
      getErrors: () => errors,
      getAdditional: () => additionalData,
      getAxiosPrivate: () => axiosPrivate,
      getValues: (path) => {
        return getValues(path);
      },
      addRow: (newR) => {
        if (newR && Object.keys(newR)?.length > 0) {
          if (
            ["analysis", "newAnalysisProtocol"].includes(type) &&
            newR?.items?.length > 0
          ) {
            newR?.items?.forEach((exam) => {
              const id = exam?.id;
              newR.id = id;
              newR.substance_id = id;
              newR.value = id;
              newR.removed = false;

              setValue(`column.${id}`, { ...newR });
              setValue(`removed.${id}`, false);
            });
          } else {
            setValue(`column.${newR?.value}.value`, newR.quantity);
            setValue(`removed.${newR?.value}`, false);
            
            if (newR.opType === "PROTOCOL") {
              newR.id = newR.value;
              setValue(`column.${newR?.value}`, newR);
            }
          }
        }
        const setFunc = (old) => [
          ...old,
          newR && Object.keys(newR)?.length > 0 ? newR : newRow,
        ];
        setData(setFunc);
        setOriginalData(setFunc);
      },
      selectRow: (rowIndex, selected) => {
        const id =
          table.getRow(rowIndex).original?.substance_id ||
          table.getRow(rowIndex).original?.value ||
          table.getRow(rowIndex).original.id;
        setValue(`removed.${id}.value`, selected);
      },
      removeRow: (rowIndex) => {
        const row = table.getRow(rowIndex).original;
        if (type === "filters") {
          const isCandle = type === "filters" ? ["NOMINAL", "ABSOLUTE"].includes(row?.type) : false;
          return removeRowHandler(row.id, isCandle);
        }
        if (["caps", "bottles", "treatments"].includes(type))
          return removeRowHandler(row.id);

        if (
          ["analysis", "newAnalysisProtocol"].includes(type) &&
          row?.items?.length > 0
        ) {
          row?.items?.forEach((exam) => {
            const id = exam?.id || exam?.substance_id;
            if (id !== undefined) setValue(`removed.${id}`, true);
          });
        } else {
          const id =
            table.getRow(rowIndex).original.value ||
            table.getRow(rowIndex).original.id ||
            table.getRow(rowIndex).original.substance_id;

          setValue(`removed.${id}`, true);
        }
        const setFilterFunc = (old) =>
          old.filter((_row, index) => index !== rowIndex);

        setData(setFilterFunc);
        setOriginalData(setFilterFunc);

        if ("winemaking" === type || isWineMakingOperations)
          setRows(
            data
              ?.filter((op, index) => index !== rowIndex)
              ?.sort((a, b) => (a.day > b.day ? 1 : b.day > a.day ? -1 : 0))
          );
      },
      removeSelectedRows: (selectedRows) => {
        selectedRows?.forEach((rowIndex) => {
          const row = table.getRow(rowIndex).original;
          if (
            ["analysis", "newAnalysisProtocol"].includes(type) &&
            row?.items?.length > 0
          ) {
            row?.items?.forEach((exam) => {
              const id = exam?.id || exam?.substance_id;
              if (id !== undefined) setValue(`removed.${id}`, true);
            });
          } else {
            const id = ["analysis", "treatment"].includes(type)
              ? row?.substance_id
              : row?.value || row?.id;
            if (id !== undefined) setValue(`removed.${id}`, true);
          }
        });

        const setFilterFunc = (old) =>
          old.filter((_row, index) => !selectedRows.includes(index));
        setData(setFilterFunc);
        setOriginalData(setFilterFunc);

        if ("winemaking" == type || isWineMakingOperations)
          setRows(
            data
              ?.filter((op, index) => !selectedRows.includes(index))
              ?.sort((a, b) => (a.day > b.day ? 1 : b.day > a.day ? -1 : 0))
          );
      },
    },
  });

  const ref = useRef(null);

  const setGlobalFilterByValue = (value) => {
    setGlobalFilter(String(value));
    setFilterOpened("");
    setIsFilters(String(value)?.length > 0);
  };

  const renderSearchBar = () => {
    return (
      <div className="search-box">
        <button
          className="btn-search"
          onClick={() => {
            ref.current?.focus();
          }}
        >
          <Icon icon="fa-search" size="lg" />
        </button>
        <DebouncedInput
          forwardRef={ref}
          value={globalFilter ?? ""}
          onChange={(value) => setGlobalFilterByValue(value)}
          className="input-search"
          placeholder={getLabel("search_Placeholder")}
        />
      </div>
    );
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const resetFilters = () => {
    table.resetColumnFilters();
    table.resetGlobalFilter();
    setFilterOpened("");
    setIsFilters(false);
  };

  const [isGrouped, setIsGrouped] = useState(false);

  const groupOperations = () => {
    if (groupHandler) {
      const filteredData = groupHandler(data);
      if (!isGrouped) setData(filteredData);
      else setData(defaultData);
      setIsGrouped((prev) => !prev);
    }
  };

  const renderFilters = () => {
    return (
      <div className="filter-box">
        <div className="btn-filter">
          {groupHandler && (
            <Tooltip
              variant={"info"}
              html={getLabel(
                !isGrouped
                  ? "filtersRemoveRecurrentOpsTooltip"
                  : "filtersRestoreRecurrentOpsTooltip"
              )}
              place="left"
              events={["hover"]}
            >
              <CTA
                customClassName={`${isGrouped ? "complete" : ""}`}
                onClick={() => groupOperations()}
              >
                <Icon
                  icon={
                    isGrouped ? "fa-arrows-maximize" : "fa-arrows-minimize"
                  }
                />
              </CTA>
            </Tooltip>
          )}
          <Tooltip
            variant={"info"}
            html={getLabel("filtersRemoveTooltip")}
            place="left"
            events={["hover"]}
          >
            <CTA
              className="remove"
              disabled={!isFilters}
              onClick={() => resetFilters()}
            >
              <Icon icon="fa-filter-circle-xmark" />
            </CTA>
          </Tooltip>
          {type === "lot" && false && (
            <Tooltip
              variant={"info"}
              html={getLabel(
                isAdditionalFilter
                  ? "filtersAddLotFilterTooltip"
                  : "filtersRemoveLotFilterTooltip"
              )}
              place="left"
              events={["hover"]}
            >
              <CustomFilter />
            </Tooltip>
          )}
        </div>
        {/* <TableFilterModal
          isOpen={isOpen}
          onConfirm={null}
          onClose={handleCloseModal}
          description={getLabel("modalFilterDescription")}
        >
          {filterType === "lots" && (
            <>
            </>
          )}
        </TableFilterModal> */}
      </div>
    );
  };

  const renderLegend = () => {
    return (
      <div>
        <h5>{getLabel("legend")}</h5>
        <ul>
          <li>
            <span>{getLabel("filtersOn")}</span>
            <span className="icon red">
              <Icon                 icon="fa-filter"
                style={{ color: "rgba(243, 239, 245, 1)" }}
                size={"xs"}
              />
            </span>
          </li>
          <li>
            <span>{getLabel("filtersOff")}</span>
            <span className="icon red">
              <Icon                 icon="fa-filter"
                style={{ color: "rgba(243, 239, 245, 0.5)" }}
                size={"xs"}
              />
            </span>
          </li>
        </ul>
      </div>
    );
  };

  const renderPaginationTools = () => {

    const savePageIndex = (index) => {
      table.setPageIndex(index);
    };

    const clientPageSize = table.getState().pagination.pageSize;
    const clientPageIndex = table.getState().pagination.pageIndex;
    const clientPagesPerBatch = serverPagination ? Math.ceil(serverPagination.batchSize / clientPageSize) : 0;

    const absolutePageIndex = serverPagination
      ? serverPagination.page * clientPagesPerBatch + clientPageIndex
      : clientPageIndex;
    const totalAbsolutePages = serverPagination
      ? Math.ceil(serverPagination.totalCount / clientPageSize)
      : table.getPageCount();

    const lastClientPageOnLastBatch = serverPagination
      ? Math.max(0, Math.ceil((serverPagination.totalCount - (serverPagination.totalPages - 1) * serverPagination.batchSize) / clientPageSize) - 1)
      : 0;

    const isFirst = !table.getCanPreviousPage() && !(serverPagination?.hasPrev);
    const isLast = !table.getCanNextPage() && !(serverPagination?.hasNext);

    return (
      <div className="pagination-items">
        <button onClick={() => serverPagination ? serverPagination.onFirst() : savePageIndex(0)} disabled={isFirst}>
          {"<<"}
        </button>
        <button
          onClick={() => table.getCanPreviousPage() ? savePageIndex(clientPageIndex - 1) : serverPagination?.onPrev()}
          disabled={!table.getCanPreviousPage() && !(serverPagination?.hasPrev)}
        >
          {"<"}
        </button>
        <button
          onClick={() => table.getCanNextPage() ? savePageIndex(clientPageIndex + 1) : serverPagination?.onNext()}
          disabled={!table.getCanNextPage() && !(serverPagination?.hasNext)}
        >
          {">"}
        </button>
        <button onClick={() => serverPagination ? serverPagination.onLast(lastClientPageOnLastBatch) : savePageIndex(table.getPageCount() - 1)} disabled={isLast}>
          {">>"}
        </button>
        <span className="current-page">
          <div>{getLabel("page")}&nbsp;</div>
          <strong>
            {absolutePageIndex + 1} {getLabel("of")} {totalAbsolutePages || 1}
          </strong>
        </span>
        <span>
          |&nbsp;&nbsp;&nbsp;{getLabel("goToPage")}:&nbsp;
          <input
            type="number"
            defaultValue={absolutePageIndex + 1}
            onChange={(e) => {
              const absPage = e.target.value ? Number(e.target.value) - 1 : 0;
              if (serverPagination) {
                const targetServer = Math.min(Math.floor(absPage / clientPagesPerBatch), serverPagination.totalPages - 1);
                const targetClient = absPage % clientPagesPerBatch;
                if (targetServer !== serverPagination.page) serverPagination.onGoTo(targetServer, targetClient);
                else savePageIndex(targetClient);
              } else {
                savePageIndex(absPage);
              }
            }}
            className="input-page"
          />
        </span>
        <select
          className="button fix-height"
          value={clientPageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {getLabel("show")} {pageSize}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const [isAdditionalFilter, setIsAdditionalFilter] = useState(null);
  const [filterOpened, setFilterOpened] = useState("");
  const [isFilters, setIsFilters] = useState(null);
  const [filters, setFilters] = useState([]);

  const handleFilter = (e, column, o = null) => {
    e.stopPropagation();
    const columnId = column.id;
    
    if (o !== null) {
      column.setFilterValue(o === "all" ? undefined : o); // trick --> '-' li All selector
      setFilterOpened("");
      setIsFilters(o === "all" ? false : true);
    } else {
      setFilterOpened(columnId);
    }
  };

  const openFilter = (e, columnId) => {
    e.stopPropagation();
    setFilterOpened(columnId);
  };

  const applyFilter = (column, value) => {
    column.setFilterValue(value === "all" ? undefined : value);
    setFilterOpened("");
    setIsFilters(value !== "all");
  };

  const handleTextChange = (column, value) => {
    column.setFilterValue(value || undefined);
    setIsFilters(Boolean(value));
  };

  const isValidDate = (d) => {
    const parsedDate = new Date(d);
    return parsedDate instanceof Date && !Number.isNaN(parsedDate);
  };

  const Filter = ({ column, rows }) => {
    const filterRef = useRef();

    useClickOutside(filterRef, () => {
      if (filterOpened) {
        setFilterOpened("");
      }
    });

    const columnFilterValue = column.getFilterValue();

    const { filterVariant, selector, array, isFilterDisabled } =
      column.columnDef.meta ?? {};

    let sortedUniqueValues = useMemo(
      () =>
        ["range", "date"].includes(filterVariant) ||
        [
          "lot_label",
          "tank_name",
          "type",
          "operation_type",
          "aggregate",
          "cellarman_ids",
        ].includes(selector)
          ? []
          : Array.from(column.getFacetedUniqueValues().keys())
              .sort()
              .slice(0, 5000),
      [column.getFacetedUniqueValues(), filterVariant]
    );
    
    const tmp = new Set();
    if (selector) {
      if (selector === "type" || selector === "operation_type") {
        data?.map((row) => {
          const opType = row?.[selector]?.split(" ")?.[0] || null;
          if (opType !== null) {
            tmp.add(getLabel(`operationType${opType}`));
          }
        });
      } else if (selector === "lot_label") {
        data?.forEach((row) => {
          const labels =
            array === "label"
              ? row?.label // in LotList
              : array?.map((l) => l?.label)?.flat(); // in TankList, so array is all lots
          labels?.forEach?.((l) => {
            if (l) tmp.add(getLabel(`lotLabel_${l}`));
          });
        });
      } else if (selector === "substance_name") {
        data?.forEach((d) =>
          d?.[array]?.map((row) => {
            tmp.add(
              array === "analysis"
                ? getLabel(`substance_${row?.substance_name}`)
                : row?.substance_name
            );
          })
        );
      } else {
        rows?.map((row) => {
          if (selector === "name") {
            if (row?.type === "BOTTLING" && array === "dest_tanks") return true;
            const arr =
              row?.[array]?.map((t) => t?.name) || [];
            if (arr?.length > 0) {
              arr.forEach((item) => tmp.add(item));
            }
          } else if (selector === "cellarman_ids") {
            const arr =
              array
                ?.filter((u) => row?.[selector].includes(u?.id))
                ?.map((u) => u?.username) || [];
            if (arr?.length > 0) {
              arr.forEach((item) => tmp.add(item));
            }
          } else if (selector === "tank_name") {
            const arr = row?.[array]?.map((t) => t?.[selector]) || [];
            if (arr?.length > 0) {
              arr.forEach((item) => tmp.add(item));
            }
          } else if (
            filterVariant === "range" &&
            selector === "actual_quantity"
          ) {
            const quantity = parseInt(row?.actual_quantity) || null;
            if (quantity !== null && quantity >= 0) {
              tmp.add(quantity);
            }
          }
        });
      }
      if (
        [
          "lot_label",
          "tank_name",
          "type",
          "operation_type",
          "name",
          "substance_name",
          "cellarman_ids",
        ].includes(selector)
      ) {
        sortedUniqueValues.push(
          ...Array.from(tmp)?.sort((a, b) =>
            a?.localeCompare(b, undefined, { numeric: true })
          )
        );
      } else if (selector === "tankType") {
        sortedUniqueValues = sortedUniqueValues?.map((s) => s?.split(" ")?.[0]);
      } else if (filterVariant === "range" && selector === "actual_quantity") {
        const arr = Array.from(tmp);
        if (arr?.length > 0) {
          sortedUniqueValues.push(Math.min(...arr), Math.max(...arr));
          // setIsFilters(true);
          // setFilterOpened(column.id);
        }
      }
    }
    
    let searchPlaceholder = `${getLabel("search_Placeholder")} `;
    if (!["aggregate", "substance_name"].includes(selector)) {
      searchPlaceholder += ` (${
        ["tank_name", "type", "operation_type", "name", "cellarman_ids"].includes(selector)
          ? sortedUniqueValues?.filter(Boolean).length
          : Array.from(column.getFacetedUniqueValues().keys()).filter(
              (val) => val !== null
            ).length
      })`;
    }

    const startDate = filterVariant === "date" ? columnFilterValue?.[0] : null;
    const endDate = filterVariant === "date" ? columnFilterValue?.[1] : null;
    // console.log(startDate, typeof startDate)

    if (isFilterDisabled) return;

    return (
      <div
        className={"filters"}
        ref={filterRef}
        onMouseEnter={() => setHeaderOver(-1)}
      >
        <div className={"dropdown-filter"}>
          <div className={"icon"} onClick={(e) => openFilter(e, column.id)}>
            <Icon               icon="fa-filter"
              size={"xs"}
              style={{
                color:
                  column.getFilterValue()?.length > 0
                    ? "rgba(243, 239, 245, 1)"
                    : "rgba(243, 239, 245, 0.5)",
              }}
            />
          </div>
          {filterOpened === column.id && (
            <div
              className={`dropdown-filter-content ${
                String(filterOpened) === column.id ? "open" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {filterVariant === "range" ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="range">
                    <DebouncedInput
                      type="number"
                      min={Number(sortedUniqueValues?.[0] ?? "")}
                      max={Number(sortedUniqueValues?.[1] ?? "")}
                      value={columnFilterValue?.[0] ?? ""}
                      onChange={(value) =>
                        column.setFilterValue((old) => [
                          Number(value),
                          old?.[1],
                        ])
                      }
                      placeholder={`Min ${
                        sortedUniqueValues?.[0]
                          ? `(${sortedUniqueValues?.[0]})`
                          : ""
                      }`}
                      className=""
                    />
                    <DebouncedInput
                      type="number"
                      min={Number(sortedUniqueValues?.[0] ?? "")}
                      max={Number(sortedUniqueValues?.[1] ?? "")}
                      value={columnFilterValue?.[1] ?? ""}
                      onChange={(value) =>
                        column.setFilterValue((old) => [
                          old?.[0],
                          Number(value),
                        ])
                      }
                      placeholder={`Max ${
                        sortedUniqueValues?.[1]
                          ? `(${sortedUniqueValues?.[1]})`
                          : ""
                      }`}
                      className=""
                    />
                  </div>
                </div>
              ) : filterVariant === "select" ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <ul>
                    <li
                      value={"all"}
                      onClick={(e) => {
                        e.stopPropagation();
                        applyFilter(column, "all");
                      }}
                      key={"li-"}
                      className={`all ${
                        columnFilterValue?.toString() ? "" : "disabled"
                      }`}
                    >
                      {getLabel("filtersAll")} <Icon icon="remove" />
                    </li>
                    {sortedUniqueValues?.map((o, i) => {
                      return (
                        <li
                          onClick={(e) => {
                            e.stopPropagation();
                            applyFilter(column, o);
                          }}
                          className={
                            columnFilterValue?.toString() === o ? "active" : ""
                          }
                          value={columnFilterValue?.toString()}
                          key={`li-${o}`}
                        >
                          {o}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : filterVariant === "text" ? (
                <div
                  onClick={(e) => handleFilter(e, column)}
                  className="debounced"
                >
                  {/* Autocomplete suggestions from faceted values feature */}
                  <datalist id={column.id + "list"} className="datalist">
                    {sortedUniqueValues.map((value) => (
                      <option value={value} key={value} />
                    ))}
                  </datalist>
                  <DebouncedInput
                    forwardRef={ref}
                    type="text"
                    debounce={1000}
                    value={columnFilterValue ?? ""}
                    onChange={(value) => handleTextChange(column, value)}
                    placeholder={searchPlaceholder}
                    className=""
                    list={column.id + "list"}
                    isAutofocus={true}
                    setFilterOpened={setFilterOpened}
                  />
                </div>
              ) : filterVariant === "date" ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="range date">
                    <label className="date-filter-field">
                      <span>{getLabel("dateFrom")}</span>
                      <DebouncedInput
                        type="date"
                        lang="it-IT"
                        value={toDateInputValue(startDate)}
                        onChange={(value) => {
                          if (value === "") {
                            column.setFilterValue((old) => [null, old?.[1]]);
                          } else if (
                            isValidDate(value) &&
                            value !== "Invalid Date"
                          ) {
                            column.setFilterValue((old) => [
                              new Date(value),
                              old?.[1],
                            ]);
                          }
                        }}
                        className=""
                      />
                    </label>
                    <label className="date-filter-field">
                      <span>{getLabel("dateTo")}</span>
                      <DebouncedInput
                        type="date"
                        lang="it-IT"
                        value={toDateInputValue(endDate)}
                        onChange={(value) => {
                          if (value === "") {
                            column.setFilterValue((old) => [old?.[0], null]);
                          } else if (
                            isValidDate(value) &&
                            value !== "Invalid Date"
                          )
                            column.setFilterValue((old) => [
                              old?.[0],
                              new Date(value),
                            ]);
                        }}
                        className=""
                      />
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    );
  };

  const CustomFilter = ({ column, rows }) => {
    const filterRef = useRef();

    useClickOutside(filterRef, () => {
      if (filterOpened) {
        setFilterOpened("");
      }
    });

    return (
    <CTA
      customClassName={`${isGrouped ? "complete" : ""}`}
      // onClick={() => groupOperations()}
    >
      <img className={`icon`} src={grapes_img} alt={"icon"} />
    </CTA>)
    
    return (
      <div
        className={"filters"}
        ref={filterRef}
        onMouseEnter={() => setHeaderOver(-1)}
      >
        <div className={"dropdown-filter"}>
          <div className={"icon"} onClick={(e) => handleFilter(e, column)}>
            <Icon               icon="fa-filter"
              size={"xs"}
              style={{
                color:
                  column.getFilterValue()?.length > 0
                    ? "rgba(243, 239, 245, 1)"
                    : "rgba(243, 239, 245, 0.5)",
              }}
            />
          </div>
          {filterOpened === column.id && (
            <div
              className={`dropdown-filter-content ${
                String(filterOpened) === column.id ? "open" : ""
              }`}
            >
              <div onClick={(e) => e.stopPropagation()}>
                  <ul>
                    <li
                      value={"all"}
                      // onClick={(e) =>
                      //   columnFilterValue?.toString()
                      //     ? handleFilter(e, column, "all")
                      //     : null
                      // }
                      key={"li-"}
                      // className={`all ${
                        // columnFilterValue?.toString() ? "" : "disabled"
                      // }`}
                    >
                      {getLabel("filtersAll")} <Icon icon="remove" />
                    </li>
                    {/* {sortedUniqueValues?.map((o, i) => {
                      return (
                        <li
                          onClick={(e) => handleFilter(e, column, o)}
                          className={
                            columnFilterValue?.toString() === o ? "active" : ""
                          }
                          value={columnFilterValue?.toString()}
                          key={`li-${o}`}
                        >
                          {o}
                        </li>
                      );
                    })} */}
                  </ul>
                </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const applyCardFilters = () => {
    const filteredData = [];
    const rows = table.getRowModel().rows;

    if (type === 'analysisDetail') {
      const tankMap = new Map();
      const tankOrder = [];
      for (const r of rows) {
        const key = `${r.original?.tank_id ?? ""}_${r.original?.batch_id ?? ""}`;
        if (!tankMap.has(key)) {
          tankMap.set(key, {
            tank_id: r.original?.tank_id,
            batch_id: r.original?.batch_id,
            src_name: r.original?._originalSrcName || r.original?.src_name || "",
            color: r.original?.color,
            analyst: r.original?.analyst,
            substances: [],
          });
          tankOrder.push(key);
        }
        tankMap.get(key).substances.push({
          name: r.original?.substance_name,
          value: r.original?.value,
          unit: r.original?.unit,
          min_range: r.original?.min_range,
          max_range: r.original?.max_range,
        });
      }
      return tankOrder.map(key => ({ original: tankMap.get(key) }));
    }

    if (filters?.length === 0) return rows;

    rows?.forEach((dd) => {
      const d = dd.original;
      let include = true;
      filters?.forEach((f) => {
        let props = "";
        // because varieties, origin and years are array for lots
        if (f?.type === "text" && f?.params?.length > 1)
          f?.params?.map(
            (pp) =>
              (props =
                props +
                (d?.[pp]
                  ? ` ${(Array.isArray(d?.[pp]) ? d?.[pp]?.toString() : d?.[pp])
                      ?.trim()
                      ?.toLowerCase()}`
                  : ""))
          );
        const prop =
          f?.type === "text" && f?.params?.length > 1
            ? props
            : d?.[f?.param]?.trim()?.toLowerCase();
        if (!prop || !prop?.includes(f?.value)) include = false;
        if (!include) return false;
      });
      if (include) filteredData.push(dd);
    });
    return filteredData;
  };

  if (isMobile)
    return (
      <div className="list-container">
        <div className="list_header_wrapper">
          <div className="card_filters_wrapper">
            <CardFilters
              type={type}
              data={data}
              appliedFilters={filters}
              setAppliedFilters={setFilters}
            />
          </div>
        </div>
        <div className="list-wrapper">
          {applyCardFilters()?.map((row, index) => (
            <ElementCard
            key={`element-card-${index}`}
              data={row.original}
              type={type}
              rowClickHandler={() => {
                if (String(filterOpened)?.length > 0) {
                  setFilterOpened("");
                  return;
                }
                if (rowClickHandler) {
                  rowClickHandler(row);
                }
              }}
              className={`${
                rowClickHandler
                  ? String(filterOpened)?.length > 0
                    ? ""
                    : "clickable"
                  : ""
              }`}
            />
          ))}
        </div>
      </div>
    );

  return (
    <>
      {(isSearchBar || isFilters) && <div className="box-wrapper">
        <div className="left-box">{isSearchBar && renderSearchBar()}</div>
        <div className="right-box">{filterType && renderFilters()}</div>
      </div>}
      <article className="table-container">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, i) => (
                  <th key={`header_${i}_${header.id}`} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? `cursor-pointer select-none ${
                                isSorting ? "sort" : ""
                              }`
                            : "",
                          onClick: isSorting
                            ? header.column.getToggleSortingHandler()
                            : () => {
                                return;
                              },
                        }}
                      >
                        <div
                          className="table-header"
                          onMouseEnter={() => setHeaderOver(i)}
                          onMouseLeave={() => setHeaderOver(-1)}
                        >
                          <div className="table-header-value">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                          {isSorting && (
                            <div className="table-header-sorting">
                              {" "}
                              {!header.column.getIsSorted() &&
                                headerOver === i && (
                                  <Icon icon="fa-sort" size={"sm"} />
                                )}
                              {!header.column.getIsSorted() &&
                                headerOver !== i && <p>&nbsp;&nbsp;</p>}{" "}
                              {/* trick to avoid translation on hover */}
                              {{
                                asc: (
                                  <Icon                                     icon="fa-sort-up"
                                    size={"sm"}
                                  />
                                ),
                                desc: (
                                  <Icon                                     icon="fa-sort-down"
                                    size={"sm"}
                                  />
                                ),
                                // asc: <> &uarr;</>,
                                // desc: <> &darr;</>,
                              }[header.column.getIsSorted()] ?? null}
                            </div>
                          )}
                          {header.column.getCanFilter() ? (
                            <Filter
                              column={header.column}
                              rows={table
                                .getFilteredRowModel()
                                ?.rows?.map((r) => r?.original)}
                            />
                          ) : null}
                        </div>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={`row_${row?.original?.value || row.id}`}
                onClick={() => {
                  if (String(filterOpened)?.length > 0) {
                    setFilterOpened("");
                    return;
                  }
                  if (rowClickHandler) {
                    rowClickHandler(row);
                  }
                }}
                className={`${
                  rowClickHandler
                    ? String(filterOpened)?.length > 0
                      ? ""
                      : "clickable"
                    : ""
                } 
                ${
                  row?.original?.endOfSrcNameBorder && sorting?.length === 0
                    ? "tankRowSpan"
                    : ""
                } 
                ${
                  row?.original?.endOfRepBorder && sorting?.length === 0
                    ? "rowSpan"
                    : ""
                }
                ${
                  row?.original?.rowBorder && sorting?.length === 0
                    ? "rowBorder"
                    : ""
                } `}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <td key={`cell_${i}_${cell.id}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {(isEditable ||
            type === "treatmentApplication" ||
            type === "treatmentApplicationUser") && (
            <tfoot>
              <tr>
                <th colSpan={table.getCenterLeafColumns().length} align="right">
                  <FooterCell
                    table={table}
                    type={
                      type === "analysisForm"
                        ? type
                        : type === "treatmentApplication"
                        ? "treatmentRecap"
                        : type === "treatmentApplicationUser"
                        ? "treatmentRecapUser"
                        : null
                    }
                  />
                </th>
              </tr>
            </tfoot>
          )}
        </table>
      </article>
      <div className="pagination">
        {isPagination && renderPaginationTools()}
      </div>
      {isLegend && (
        <div className="primary_container_lower legend">{renderLegend()}</div>
      )}
    </>
  );
};

export default TableEdit;
